import { firstValueFrom } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { environment } from '@ups/xplat/core';
import type { RynReplayTokenFactory } from '@ups/xplat/web/ryn-replay';

/**
 * Supplies the viewer's Entra access token to the Ryn Replay read sources.
 *
 * The read API validates an Entra token server-side (RynReplay:EntraAudience,
 * RynReplay:EntraTenantId) and then runs a Software Security ViewFeature check.
 * We request the workspace's standard Entra scope, the same one the MSAL
 * interceptor already attaches to first-party calls, so a viewer signed into
 * the shared Entra app can read the API.
 *
 * INFRA CONTRACT: this assumes the read API's expected audience equals
 * EntraId:Audience. If the read API is moved to a dedicated audience, change
 * the scope here and grant this app's SPA registration that API permission.
 *
 * Returns a REASON rather than a bare undefined when there is no token, so the
 * read sources can word the refusal correctly (#30126). This app is Entra-only
 * (MSAL), so it never yields `wrong-issuer`: no account means `no-session`, and
 * a failed silent acquisition means `acquire-failed`. Both resolve rather than
 * reject, because a rejection would surface as an unhandled error in the player.
 */
export function createReplayMsalTokenFactory(
  msal: MsalService
): RynReplayTokenFactory {
  const scope = `${environment.entraId?.audience}/Access.All`;
  return async () => {
    const account =
      msal.instance.getActiveAccount() ?? msal.instance.getAllAccounts()[0];
    if (!account) {
      return { reason: 'no-session' };
    }
    try {
      const result = await firstValueFrom(
        msal.acquireTokenSilent({ account, scopes: [scope] })
      );
      return result?.accessToken
        ? { token: result.accessToken }
        : { reason: 'acquire-failed' };
    } catch {
      return { reason: 'acquire-failed' };
    }
  };
}
